// src/hooks/useUnscheduledMatchCount.js
// Lightweight hook that returns how many of the user's upcoming matches
// are still missing scheduling info. Used to badge the navbar Calendar icon.
import { useState, useEffect } from 'react';
import { useTeams } from '../context/TeamContext';
import { filterUpcomingMatches, shouldShowMatch, clearSeriesCache } from '../utils/upcomingMatchesUtils';
import { supabase } from '../lib/supabase';

const isUnscheduled = (match) =>
  !match.scheduled_datetime && (!match.match_date || !match.match_time);

const useUnscheduledMatchCount = () => {
  const { userTeams } = useTeams();
  const [count, setCount] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const fetchCount = async () => {
      if (!userTeams || userTeams.length === 0) {
        setCount(0);
        return;
      }

      try {
        clearSeriesCache();
        const teamIds = userTeams.map(team => team.id);

        const { data: allMatches, error } = await supabase
          .from('matches')
          .select(`
            *,
            tournament:tournament_id(id, name, format, status),
            team1:team1_id(id),
            team2:team2_id(id)
          `)
          .or(teamIds.map(id => `team1_id.eq.${id}`).join(',') + ',' + teamIds.map(id => `team2_id.eq.${id}`).join(','))
          .order('scheduled_datetime', { ascending: true, nullsLast: true })
          .order('match_date', { ascending: true, nullsLast: true })
          .order('match_time', { ascending: true, nullsLast: true });

        if (error || cancelled) return;

        let upcoming = [];
        if (allMatches && allMatches.length > 0) {
          const candidateMatches = allMatches.filter(match =>
            shouldShowMatch(match, {
              includePastMatches: false,
              includeCompletedTournaments: false
            })
          );
          upcoming = filterUpcomingMatches(candidateMatches).filter(match =>
            match.status === 'scheduled' || match.status === 'pending'
          );
        }

        if (!cancelled) {
          setCount(upcoming.filter(isUnscheduled).length);
        }
      } catch (err) {
        if (!cancelled) setCount(0);
      }
    };

    fetchCount();
    return () => { cancelled = true; };
  }, [userTeams]);

  return count;
};

export default useUnscheduledMatchCount;
